import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import type { Action } from '@ngrx/store';
import { asyncScheduler } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

import { injectCatchHttpError } from '../../errors/catch-http-error.util';
import type { SearchItemMetaExpanded } from '../../models/common.models';
import type { ListResponse } from '../../models/responses.models';
import { CoreHelperService } from '../../services/helpers/core-helper.service';
import { addSearchItemMetaExpanded } from '../uistate/uistate.actions';
import type { PaginationItem } from './pagination.actions';
import { ItemPaginationType, getItems, getItemsFail, getItemsSuccess } from './pagination.actions';
import { CurrentUserService } from '../../../user/services/current-user.service';

@Injectable()
export class PaginationEffects {
  private http = inject(HttpClient);
  private helper = inject(CoreHelperService);
  private currentUser = inject(CurrentUserService);

  private readonly actions$ = inject<Actions>(Actions);
  private readonly catchHttpError = injectCatchHttpError();

  getAnyItems$ = createEffect(this.handleEffect(ItemPaginationType.AnyType));
  issueItems$ = createEffect(this.handleEffect(ItemPaginationType.IssueItems));
  tableItems$ = createEffect(this.handleEffect(ItemPaginationType.TableItems));
  processItems$ = createEffect(this.handleEffect(ItemPaginationType.ProcessItems));
  reportIssueItems$ = createEffect(this.handleEffect(ItemPaginationType.ReportIssueItems));
  lariWebItems$ = createEffect(this.handleEffect(ItemPaginationType.LariWebItems));
  lariInternalItems$ = createEffect(this.handleEffect(ItemPaginationType.LariInternalItems));
  projectTags$ = createEffect(this.handleEffect(ItemPaginationType.ProjectTags));
  projectsDropdown$ = createEffect(this.handleEffect(ItemPaginationType.ProjectsDropdown));
  managedUsers$ = createEffect(this.handleEffect(ItemPaginationType.ManagedUsers));
  users$ = createEffect(this.handleEffect(ItemPaginationType.Users));
  issueTemplatesDropdown$ = createEffect(this.handleEffect(ItemPaginationType.IssueTemplatesDropdown));
  reportsDropdown$ = createEffect(this.handleEffect(ItemPaginationType.ReportsDropdown));
  issuesDropdown$ = createEffect(this.handleEffect(ItemPaginationType.IssuesDropdown));
  usersDropdown$ = createEffect(this.handleEffect(ItemPaginationType.UsersDropdown));
  tablesDropdown$ = createEffect(this.handleEffect(ItemPaginationType.TablesDropdown));
  tagsDropdown$ = createEffect(this.handleEffect(ItemPaginationType.TagsDropdown));
  issueSearchItems$ = createEffect(this.handleEffect(ItemPaginationType.IssueSearchItems));
  userSelectItems$ = createEffect(this.handleEffect(ItemPaginationType.UserSelectItems));
  monitoringItems$ = createEffect(this.handleEffect(ItemPaginationType.MonitoringItems));
  filteredTablesDropdown$ = createEffect(this.handleEffect(ItemPaginationType.FilteredTablesDropdown));
  projectSelectItems$ = createEffect(this.handleEffect(ItemPaginationType.ProjectSelectItems));
  tickets$ = createEffect(this.handleEffect(ItemPaginationType.Tickets));
  templatesDropdown$ = createEffect(this.handleEffect(ItemPaginationType.TemplatesDropdown));
  commonTemplates$ = createEffect(this.handleEffect(ItemPaginationType.CommonTemplates));
  logs$ = createEffect(this.handleEffect(ItemPaginationType.Logs));
  tableVersions$ = createEffect(this.handleEffect(ItemPaginationType.TableVersions));
  lariItemLogs$ = createEffect(this.handleEffect(ItemPaginationType.LariItemLogs));

  getOptionalActions<T extends PaginationItem>(itemType: ItemPaginationType, response: ListResponse<T>): Action[] {
    if (itemType !== ItemPaginationType.IssueSearchItems) {
      return [];
    }

    const expandedState = response.results.map<SearchItemMetaExpanded>(i => ({
      attachmentId: String(i.id),
      isExpanded: this.currentUser.user.issue_searchitem_expanded,
    }));
    return [addSearchItemMetaExpanded({ data: expandedState })];
  }

  handleEffect(itemType: ItemPaginationType, debounceNumber = 300) {
    return ({ debounce = debounceNumber } = {}) =>
      this.actions$.pipe(
        ofType(getItems(itemType)),
        debounceTime(debounce, asyncScheduler),
        switchMap(({ data }) => {
          const sParams = this.helper.formatters.convertObjToQueryString(data.queryParams);
          return this.http.get<ListResponse<PaginationItem>>(`${data.url}/${sParams}`).pipe(
            switchMap((response: ListResponse<PaginationItem>) => {
              const mainAction = getItemsSuccess(itemType)({
                data: { response, shouldAddToList: data.shouldAddToList },
              });
              const issueSearchItemsActions = this.getOptionalActions(itemType, response);
              return [mainAction, ...issueSearchItemsActions];
            }),
            this.catchHttpError(() => getItemsFail(itemType)()),
          );
        }),
      );
  }
}
